'use client';

import { useMemo } from 'react';
import Link from 'next/link';
import type { LucideIcon } from 'lucide-react';
import {
  ChevronsUpDown,
  Home,
  LogOut,
  MessageCircleQuestion,
  UserCircle2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { JWTUserData } from '@kit/supabase/types';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@kit/ui/dropdown-menu';
import { If } from '@kit/ui/if';
import { SubMenuModeToggle } from '@kit/ui/mode-toggle';
import { ProfileAvatar } from '@kit/ui/profile-avatar';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import { usePersonalAccountData } from '../hooks/use-personal-account-data';

/**
 * A titled group of navigation entries rendered as a submenu of the account
 * dropdown. Used by the app shell to fold the "My Profile" and "Account"
 * sidebar groups into this menu (2026-07-05 nav consolidation).
 */
export interface AccountMenuGroup {
  /** i18n key for the submenu label (falls back to the raw string). */
  i18nKey: string;
  icon?: LucideIcon;
  items: Array<{
    i18nKey: string;
    path: string;
    icon?: LucideIcon;
  }>;
}

export function PersonalAccountDropdown({
  className,
  user,
  signOutRequested,
  showProfileName = true,
  paths,
  features,
  account,
  dancerProfileHref,
  menuGroups,
  extraItems,
}: {
  user: JWTUserData;

  account?: {
    id: string | null;
    name: string | null;
    picture_url: string | null;
  };

  signOutRequested: () => unknown;

  paths: {
    home: string;
  };

  features: {
    enableThemeToggle: boolean;
  };

  showProfileName?: boolean;

  /**
   * When the signed-in user is a dancer with a public handle, the resolved
   * path to their own public profile (`/<handle>`). Renders a "My Profile"
   * menu entry that links straight to the public page (BAL-364). Omitted /
   * null for non-dancers — the entry is simply not shown.
   */
  dancerProfileHref?: string | null;

  /**
   * Titled navigation groups rendered as submenus between the Home entry and
   * the documentation entry — the consolidated "My Profile" / "Account"
   * sections the sidebar no longer carries. Empty groups are skipped.
   */
  menuGroups?: AccountMenuGroup[];

  /**
   * App-specific menu entries (DropdownMenuItem / DropdownMenuSub nodes)
   * rendered after the menu groups — e.g. the super-admin impersonate
   * submenu.
   */
  extraItems?: React.ReactNode;

  className?: string;
}) {
  const { t } = useTranslation();
  const { data: personalAccountData } = usePersonalAccountData(
    user.id,
    account,
  );

  const signedInAsLabel = useMemo(() => {
    const email = user?.email ?? undefined;
    const phone = user?.phone ?? undefined;

    return email ?? phone;
  }, [user]);

  const displayName =
    personalAccountData?.name ?? account?.name ?? user?.email ?? '';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t('common:openProfileMenu')}
        data-test={'account-dropdown-trigger'}
        className={cn(
          'group/trigger fade-in focus:outline-primary flex cursor-pointer items-center group-data-[minimized=true]/sidebar:px-0',
          className ?? '',
          {
            ['active:bg-secondary/50 items-center gap-4 rounded-md' +
            ' hover:bg-secondary border border-dashed p-2 transition-colors']:
              showProfileName,
          },
        )}
      >
        <ProfileAvatar
          className={
            'group-hover/trigger:border-background/50 rounded-md border border-transparent transition-colors'
          }
          fallbackClassName={'rounded-md border'}
          displayName={displayName ?? user?.email ?? ''}
          pictureUrl={personalAccountData?.picture_url}
        />

        <If condition={showProfileName}>
          <div
            className={
              'fade-in flex w-full flex-col truncate text-left group-data-[minimized=true]/sidebar:hidden'
            }
          >
            <span
              data-test={'account-dropdown-display-name'}
              className={'truncate text-sm'}
            >
              {displayName}
            </span>

            <span
              data-test={'account-dropdown-email'}
              className={'text-muted-foreground truncate text-xs'}
            >
              {signedInAsLabel}
            </span>
          </div>

          <ChevronsUpDown
            className={
              'text-muted-foreground mr-1 h-8 group-data-[minimized=true]/sidebar:hidden'
            }
          />
        </If>
      </DropdownMenuTrigger>

      <DropdownMenuContent className={'xl:min-w-[15rem]!'}>
        <DropdownMenuItem className={'h-10! rounded-none'}>
          <div
            className={'flex flex-col justify-start truncate text-left text-xs'}
          >
            <div className={'text-muted-foreground'}>
              <Trans i18nKey={'common:signedInAs'} />
            </div>

            <div>
              <span className={'block truncate'}>{signedInAsLabel}</span>
            </div>
          </div>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem asChild>
          <Link
            className={'s-full flex cursor-pointer items-center space-x-2'}
            href={paths.home}
          >
            <Home className={'h-5'} />

            <span>
              <Trans i18nKey={'common:routes.home'} />
            </span>
          </Link>
        </DropdownMenuItem>

        <If condition={!!dancerProfileHref}>
          <DropdownMenuItem asChild>
            <Link
              className={'s-full flex cursor-pointer items-center space-x-2'}
              href={dancerProfileHref ?? '#'}
            >
              <UserCircle2 className={'h-5'} />

              <span>
                <Trans
                  i18nKey={'common:routes.myProfile'}
                  defaults={'My Profile'}
                />
              </span>
            </Link>
          </DropdownMenuItem>
        </If>

        {(menuGroups ?? [])
          .filter((group) => group.items.length > 0)
          .map((group) => {
            const GroupIcon = group.icon;

            return (
              <DropdownMenuSub key={group.i18nKey}>
                <DropdownMenuSubTrigger className={'cursor-pointer'}>
                  <span className={'flex items-center space-x-2'}>
                    {GroupIcon ? <GroupIcon className={'h-5'} /> : null}

                    <span>
                      <Trans i18nKey={group.i18nKey} defaults={group.i18nKey} />
                    </span>
                  </span>
                </DropdownMenuSubTrigger>

                <DropdownMenuPortal>
                  <DropdownMenuSubContent>
                    {group.items.map((item) => {
                      const ItemIcon = item.icon;

                      return (
                        <DropdownMenuItem key={item.path} asChild>
                          <Link
                            className={
                              's-full flex cursor-pointer items-center space-x-2'
                            }
                            href={item.path}
                          >
                            {ItemIcon ? <ItemIcon className={'h-5'} /> : null}

                            <span>
                              <Trans
                                i18nKey={item.i18nKey}
                                defaults={item.i18nKey}
                              />
                            </span>
                          </Link>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
            );
          })}

        {extraItems}

        <DropdownMenuSeparator />

        <DropdownMenuItem asChild>
          <Link
            className={'s-full flex cursor-pointer items-center space-x-2'}
            href={'/docs'}
          >
            <MessageCircleQuestion className={'h-5'} />

            <span>
              <Trans i18nKey={'common:documentation'} />
            </span>
          </Link>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <If condition={features.enableThemeToggle}>
          <SubMenuModeToggle />
        </If>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          data-test={'account-dropdown-sign-out'}
          role={'button'}
          className={'cursor-pointer'}
          onClick={signOutRequested}
        >
          <span className={'flex w-full items-center space-x-2'}>
            <LogOut className={'h-5'} />

            <span>
              <Trans i18nKey={'auth:signOut'} />
            </span>
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
